import { lazy, Suspense } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import Layout from './components/Layout';
import PageTransition from './components/PageTransition';
import LoadingSpinner from './components/LoadingSpinner';
import ScrollToTop from './components/ScrollToTop';
import { Switch, Route } from 'wouter';

// Lazy-loaded page components for code splitting
const HomePage = lazy(() => import('./pages/Home.tsx'));
const AboutPage = lazy(() => import('./pages/About.tsx'));
const ServicesPage = lazy(() => import('./pages/Services.tsx'));
const ServiceDetailPage = lazy(() => import('./pages/ServiceDetail.tsx'));
const PortfolioPage = lazy(() => import('./pages/Portfolio.tsx'));
const PortfolioDetailPage = lazy(() => import('./pages/PortfolioDetail.tsx'));
const ContactPage = lazy(() => import('./pages/Contact.tsx'));
const DomainPage = lazy(() => import('./pages/Domain.tsx'));
const HostingPage = lazy(() => import('./pages/hosting.tsx'));
const DomainSearchPage = lazy(() => import('./pages/domain-search.tsx'));
const SitemapPage = lazy(() => import('./pages/Sitemap.tsx'));
const PrivacyPolicyPage = lazy(() => import('./pages/PrivacyPolicy.tsx'));
const TermsOfServicePage = lazy(() => import('./pages/TermsOfService.tsx'));
const CookiePolicyPage = lazy(() => import('./pages/CookiePolicy.tsx'));
const FAQPage = lazy(() => import('./pages/FAQ.tsx'));
const GuidesPage = lazy(() => import('./pages/Guides.tsx'));
const SupportPage = lazy(() => import('./pages/Support.tsx'));
const BlogPage = lazy(() => import('./pages/Blog.tsx'));
const EnquiryPage = lazy(() => import('./pages/Enquiry.tsx'));
const NotFound = lazy(() => import('./pages/not-found.tsx'));

/**
 * Main App component for DWB client.
 * Sets up routing, layout, theme context, and page transitions.
 *
 * @returns {JSX.Element} The root application component.
 */

export default function App() {
  return (
    // Provide theme context to all components
    <ThemeProvider>
      {/* Scroll to top on route changes */}
      <ScrollToTop />
      {/* Main layout wrapper */}
      <Layout>
        {/* Handles animated page transitions */}
        <PageTransition>
          {/* Suspense boundary for lazy-loaded routes */}
          <Suspense fallback={
              <div className="flex items-center justify-center min-h-[60vh]">
                <LoadingSpinner size="lg" message="Loading..." />
              </div>
            }>
            {/* Client-side routing for all main pages */}
            <Switch>
              <Route path="/" component={HomePage} />
              <Route path="/about" component={AboutPage} />
              <Route path="/services" component={ServicesPage} />
              <Route path="/services/:slug" component={ServiceDetailPage} />
              <Route path="/portfolio" component={PortfolioPage} />
              <Route path="/portfolio/:id" component={PortfolioDetailPage} />
              <Route path="/contact" component={ContactPage} />
              <Route path="/domain" component={DomainPage} />
              <Route path="/hosting" component={HostingPage} />
              <Route path="/domain-search" component={DomainSearchPage} />
              <Route path="/sitemap" component={SitemapPage} />
              <Route path="/privacy-policy" component={PrivacyPolicyPage} />
              <Route path="/terms-of-service" component={TermsOfServicePage} />
              <Route path="/cookie-policy" component={CookiePolicyPage} />
              <Route path="/faq" component={FAQPage} />
              <Route path="/guides" component={GuidesPage} />
              <Route path="/support" component={SupportPage} />
              <Route path="/blog" component={BlogPage} />
              <Route path="/enquiry" component={EnquiryPage} />
              <Route path="/quote" component={EnquiryPage} />
              <Route component={NotFound} />
            </Switch>
          </Suspense>
        </PageTransition>
      </Layout>
    </ThemeProvider>
  );
}